import { selectUserVmForDocumentList } from '@newStore/documentList/newDocumentListSelectors';
import { setFilterProperty } from '@newStore/documentList/newDocumentListState';
import { selectUser } from '@newStore/user/userSelectors';
import { AsyncKovResourcePicker } from '@kathondvla/react-shared-components/src/components';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { nlBE } from 'date-fns/locale/nl-BE';
import { useLoadSriOptions } from '@kathondvla/react-shared-components/src/components';
import * as constants from '@store/constants/constants';
import { searchApi, contentApi } from '@store/api/apiConfig';
import type React from 'react';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { datePickerDefaultOptions } from '@UI/aside/asideDatePicker/dateHelper';
import { PersonOption } from '@UI/personPicker/PersonOption';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import './filtersBar.scss';

const emptyObj = {};

// Constants
const queryAuthorsParameters = constants.allAuthorsParameters;
const queryThemesParameters = constants.queryThemesParameters;

const FiltersBar: React.FC = () => {
  const dispatch = useDispatch();
  
  const types = useTypedSelector((state) => state.newDocumentList.searchParams?.types || emptyObj);
  const q = useTypedSelector((state) => state.newDocumentList.searchParams?.q || '');
  const theme = useTypedSelector((state) => state.newDocumentList.searchParams?.theme);
  const issued = useTypedSelector((state) => state.newDocumentList.searchParams?.issued);
  const modifiedFrom = useTypedSelector((state) => state.newDocumentList.searchParams?.modifiedFrom);
  const modifiedTo = useTypedSelector((state) => state.newDocumentList.searchParams?.modifiedTo);
  const author = useTypedSelector((state) => state.newDocumentList.searchParams?.author);
  const groupedSearchables = useTypedSelector(selectUserVmForDocumentList).searchCategories;
  const loggedUser = useTypedSelector(selectUser);
  
  const [searchQuery, setSearchQuery] = useState(q);

  const doSearch = (event: React.KeyboardEvent | React.MouseEvent, type?: string) => {
    if (type === 'documenttype') {
      dispatch(setFilterProperty({ types, q: searchQuery }));
    } else if (event.type === 'click' || (event.type === 'keyup' && (event as React.KeyboardEvent).key === 'Enter')) {
      dispatch(setFilterProperty({ q: searchQuery }));
    }
  };

  const issuedChanged = (date: Date | null) => {
    dispatch(setFilterProperty({ issued: date?.toISOString() || null }));
  };

  const modifiedFromChanged = (date: Date | null) => {
    dispatch(setFilterProperty({ modifiedFrom: date?.toISOString() || null }));
  };

  const modifiedToChanged = (date: Date | null) => {
    dispatch(setFilterProperty({ modifiedTo: date?.toISOString() || null }));
  };

  const setUserAsAuthor = () => {
    if (loggedUser) {
      dispatch(setFilterProperty({ author: loggedUser }));
    }
  };

  const authorSelected = (selectedAuthor) => {
    if ((selectedAuthor || author) && selectedAuthor !== author) {
      dispatch(setFilterProperty({ author: selectedAuthor }));
    }
  };

  const themeSelected = (selectedTheme) => {
    if ((selectedTheme || theme) && selectedTheme !== theme) {
      dispatch(setFilterProperty({ theme: selectedTheme.$$expanded }));
    }
  };

  const handleTypeChange = (searchable, checked: boolean) => {
    const newTypes = { ...types, [searchable.type]: checked };
    dispatch(setFilterProperty({ types: newTypes, q: searchQuery }));
  };

  const sriLoadOptionsAuthor = useLoadSriOptions({
    sriClient: searchApi,
    href: '/search',
    parameters: queryAuthorsParameters,
  });

  const sriLoadOptionsTheme = useLoadSriOptions({
    sriClient: contentApi,
    href: '/content',
    parameters: queryThemesParameters,
  });

  return (
    <div className="col-lg-3 col-md-3 has-scrollbar filter-panel">
      <form>
        <div className="form-group">
          <div className="form-flex">
            <input
              type="search"
              placeholder="Zoek op titel..."
              className="form-control"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyUp={(e) => doSearch(e)}
              style={{ marginRight: '-5px' }}
            />
            <button
              type="button"
              className="btn btn-default btn-icon"
              onClick={(e) => doSearch(e)}
            >
              <span className="sr-only">Zoeken</span>
              <span className="glyphicon glyphicon-search" />
            </button>
          </div>
        </div>

        <div className="form-group form-group-block-labels">
          <label><b>Type</b></label>
          {groupedSearchables?.map((group) => 
            group.searchables.length > 0 && (
              <div key={group.searchables[0]?.type || 'group'}>
                {group.searchables
                  .sort((a, b) => a.information.single.localeCompare(b.information.single))
                  .map((searchable) => (
                    <div key={searchable.type} className="checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={types[searchable.type] || false}
                          onChange={(e) => handleTypeChange(searchable, e.target.checked)}
                        />
                        {searchable.information.plural}
                      </label>
                    </div>
                  ))}
                <hr/>
              </div>
            )
          )}
        </div>

        <div className="form-group filters">
          <div>
            <label>Thema</label>
          </div>
          <div>
            <AsyncKovResourcePicker
              loadOptions={sriLoadOptionsTheme}
              value={theme}
              onChange={themeSelected}
              onRemove={() => themeSelected(null)}
              placeholder="Thema"
              multiple={false}
              minimumInputLength={2}
              limit={25}
              clearable={true}
              optionTemplate={(option) => option.$$expanded?.title || option.title}
            />
          </div>
        </div>

        <div className="form-group filters">
          <div>
            <label>Publicatiedatum</label>
          </div>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlBE}>
            <DatePicker
              value={issued ? new Date(issued) : null}
              onChange={issuedChanged}
              format="dd/MM/yyyy"
              slotProps={datePickerDefaultOptions}
            />
          </LocalizationProvider>
        </div>

        <div className="form-group filters filter-modified">
          <div>
            <label>Gewijzigd</label>
          </div>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlBE}>
            <DatePicker
              value={modifiedFrom ? new Date(modifiedFrom) : null}
              onChange={modifiedFromChanged}
              format="dd/MM/yyyy"
              slotProps={datePickerDefaultOptions}
            />
          </LocalizationProvider>
          <div>-</div>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlBE}>
            <DatePicker
              value={modifiedTo ? new Date(modifiedTo) : null}
              onChange={modifiedToChanged}
              format="dd/MM/yyyy"
              slotProps={datePickerDefaultOptions}
            />
          </LocalizationProvider>
        </div>

        <div className="form-group filters">
          <div>
            <label>Auteur</label>
          </div>
          <div className="form-flex">
            <div style={{ width: '100%', marginRight: '-4px' }}>
              <AsyncKovResourcePicker
                loadOptions={sriLoadOptionsAuthor}
                value={author}
                onChange={authorSelected}
                onRemove={() => authorSelected(null)}
                placeholder="Auteur"
                multiple={false}
                minimumInputLength={2}
                limit={25}
                clearable={true}
                optionTemplate={(option) => (
                  <PersonOption
                    option={option}
                    isMismatch={false}
                  />
                )}
              />
            </div>
            <button
              style={{ height: '38px', maxHeight: '38px' }}
              type="button"
              className="btn btn-default btn-icon"
              onClick={setUserAsAuthor}
              disabled={!loggedUser}
            >
              <span className="glyphicon glyphicon-user" />
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default FiltersBar;

FiltersBar.displayName = 'FiltersBar';